import React from "react";
import { Star } from "lucide-react";
import { motion } from "framer-motion";

/**
 * A reusable, animated testimonial card. Themed for the Knight Media Group
 * dark high-contrast palette (#0A0A0A bg, #1440E1 brand) while keeping the
 * original light-mode styling intact when used outside this site.
 */
const TestimonialCard = ({
  quote,
  authorName,
  authorTitle,
  avatarUrl,
  rating,
  index,
}) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.1,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      className="flex h-full flex-col justify-between rounded-2xl bg-white p-8 shadow-sm dark:bg-white/[0.04] dark:border dark:border-white/10 dark:backdrop-blur-xl"
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      whileHover={{
        scale: 1.05,
        y: -8,
        boxShadow:
          "0px 28px 70px rgba(20,64,225,0.55), 0px 0px 0px 1px rgba(20,64,225,0.6) inset",
      }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      data-testid={`testimonial-card-${index}`}
    >
      <div>
        <div className="flex items-center gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`h-5 w-5 ${
                i < rating
                  ? "text-yellow-400 dark:text-[#1440E1]"
                  : "text-gray-300 dark:text-white/15"
              }`}
              fill="currentColor"
            />
          ))}
        </div>
        <blockquote className="mt-6 text-lg leading-relaxed text-gray-600 dark:text-white/85">
          <p>&ldquo;{quote}&rdquo;</p>
        </blockquote>
      </div>
      <footer className="mt-8">
        <div className="flex items-center">
          <img
            className="h-12 w-12 flex-shrink-0 rounded-full object-cover ring-2 ring-transparent dark:ring-[#1440E1]/40"
            src={avatarUrl}
            alt={`Avatar of ${authorName}`}
            onError={(e) => {
              e.currentTarget.src = `https://placehold.co/48x48/1440E1/FFFFFF?text=${authorName.charAt(0)}`;
              e.currentTarget.onerror = null;
            }}
          />
          <div className="ml-4">
            <p className="font-semibold text-gray-900 dark:text-white">
              {authorName}
            </p>
            <p className="text-sm text-gray-500 dark:text-white/55">
              {authorTitle}
            </p>
          </div>
        </div>
      </footer>
    </motion.div>
  );
};

export default TestimonialCard;
